import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  attachFromGlobalMaterial,
  attachFromGlobalTool,
  createMaterial,
  createTool,
  deleteMaterial,
  deleteTool,
  linkCatalogMaterialToGlobal,
  linkCatalogToolToGlobal,
  updateMaterial,
  updateTool,
} from '@/api/catalog'
import { PROJECTS_QUERY_KEY } from './use-projects'
import { patchCatalogMaterial, patchCatalogTool } from './cache-transforms'
import type {
  AttachFromGlobalMaterialRequest,
  AttachFromGlobalToolRequest,
  CreateCatalogMaterialRequest,
  CreateCatalogToolRequest,
  LinkCatalogMaterialRequest,
  LinkCatalogToolRequest,
  ProjectDetail,
  UpdateCatalogMaterialRequest,
  UpdateCatalogToolRequest,
} from '@/api/types'

const detailKey = (projectId: string) => [...PROJECTS_QUERY_KEY, 'detail', projectId]

interface CatalogPatchCtx {
  previous: ProjectDetail | undefined
}

export const useCreateMaterial = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCatalogMaterialRequest) => createMaterial(projectId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(projectId) }),
  })
}

export const useUpdateMaterial = (projectId: string) => {
  const qc = useQueryClient()
  const key = detailKey(projectId)
  return useMutation<
    unknown,
    unknown,
    { id: string; data: UpdateCatalogMaterialRequest },
    CatalogPatchCtx
  >({
    mutationFn: ({ id, data }) => updateMaterial(id, data),
    onMutate: async ({ id, data }) => {
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<ProjectDetail>(key)
      if (previous) {
        const partial: Record<string, unknown> = {}
        if (data.name !== undefined) partial.name = data.name
        if (data.unit !== undefined) partial.unit = data.unit
        if (data.unitPriceCents !== undefined) partial.unitPriceCents = data.unitPriceCents
        if (data.externalUrl !== undefined) {
          partial.externalUrl = data.externalUrl === '' ? null : data.externalUrl
        }
        qc.setQueryData<ProjectDetail>(key, patchCatalogMaterial(previous, id, partial))
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  })
}

export const useDeleteMaterial = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteMaterial(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(projectId) }),
  })
}

export const useCreateTool = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateCatalogToolRequest) => createTool(projectId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(projectId) }),
  })
}

export const useUpdateTool = (projectId: string) => {
  const qc = useQueryClient()
  const key = detailKey(projectId)
  return useMutation<
    unknown,
    unknown,
    { id: string; data: UpdateCatalogToolRequest },
    CatalogPatchCtx
  >({
    mutationFn: ({ id, data }) => updateTool(id, data),
    onMutate: async ({ id, data }) => {
      await qc.cancelQueries({ queryKey: key })
      const previous = qc.getQueryData<ProjectDetail>(key)
      if (previous) {
        const partial: Record<string, unknown> = {}
        if (data.name !== undefined) partial.name = data.name
        if (data.unitPriceCents !== undefined) partial.unitPriceCents = data.unitPriceCents
        if (data.externalUrl !== undefined) {
          partial.externalUrl = data.externalUrl === '' ? null : data.externalUrl
        }
        qc.setQueryData<ProjectDetail>(key, patchCatalogTool(previous, id, partial))
      }
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous)
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  })
}

export const useDeleteTool = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteTool(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(projectId) }),
  })
}

// ---- Phase 15: global catalog binding -------------------------------------

export const useAttachFromGlobalMaterial = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (request: AttachFromGlobalMaterialRequest) =>
      attachFromGlobalMaterial(projectId, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(projectId) }),
  })
}

export const useAttachFromGlobalTool = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (request: AttachFromGlobalToolRequest) => attachFromGlobalTool(projectId, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(projectId) }),
  })
}

export const useLinkProjectMaterialToGlobal = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      materialId,
      request,
    }: {
      materialId: string
      request: LinkCatalogMaterialRequest
    }) => linkCatalogMaterialToGlobal(projectId, materialId, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(projectId) }),
  })
}

export const useLinkProjectToolToGlobal = (projectId: string) => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ toolId, request }: { toolId: string; request: LinkCatalogToolRequest }) =>
      linkCatalogToolToGlobal(projectId, toolId, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: detailKey(projectId) }),
  })
}
